import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Sparkles, HelpCircle, Shuffle, TrendingUp, BookOpen,
  Bell, PartyPopper, ChevronRight, Lightbulb, Settings,
} from 'lucide-react'
import { TIPS } from '../lib/tips'

// Mapping from tip.icon string → lucide component so the catalogue
// in lib/tips.js stays UI-free. New tip uses an icon we haven't
// mapped yet → fall back to Lightbulb (sane "tip" symbol).
const ICONS = {
  Sparkles, HelpCircle, Shuffle, TrendingUp, BookOpen,
  Bell, PartyPopper, ChevronRight, Lightbulb, Settings,
}

// How long each tip stays on screen before the next one slides in.
// Long enough to actually read 1-2 sentences in either language;
// short enough that the user notices the rotation.
const ROTATE_MS = 5000
// Match this with the CSS keyframes durations below. Keep it small so
// the swap feels snappy.
const ANIM_MS = 350

// Rotating tip card on the home page. Cycles through every entry in
// the TIPS catalogue with a small fade/slide animation between tips.
// The whole card is the click target (it navigates to tip.route) —
// the corner chevron is the only affordance signalling "tap me." No
// dismiss; these are persistent feature pointers, not a one-shot tour.
//
// Fixed height (min-h-[140px] on mobile, min-h-[120px] on sm+) so the
// home layout doesn't jump as content length varies between tips.
export default function TipCard() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'

  const [index, setIndex] = useState(0)
  const [phase, setPhase] = useState('in') // 'in' | 'out' — drives the keyframe class
  const timeoutRef = useRef(null)

  useEffect(() => {
    if (TIPS.length <= 1) return undefined
    const scheduleNext = () => {
      timeoutRef.current = setTimeout(() => {
        setPhase('out')
        timeoutRef.current = setTimeout(() => {
          setIndex((i) => (i + 1) % TIPS.length)
          setPhase('in')
          scheduleNext()
        }, ANIM_MS)
      }, ROTATE_MS)
    }
    scheduleNext()
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current) }
  }, [])

  if (!TIPS.length) return null

  const tip = TIPS[index]
  const Icon = ICONS[tip.icon] || Lightbulb

  return (
    <button
      type="button"
      onClick={() => navigate(tip.route)}
      className="group relative w-full text-start rounded-lg border border-app-primary-500/30 bg-app-primary-500/5 px-3.5 py-3 mb-3 overflow-hidden hover:border-app-primary-500/60 hover:bg-app-primary-500/10 transition-colors"
      dir="auto"
      aria-label={tip.title[locale] || tip.title.en}
    >
      <span
        className="absolute top-2.5 end-2.5 text-app-primary-500/50 group-hover:text-app-primary-500 transition-colors"
        aria-hidden="true"
      >
        <ChevronRight className="w-3.5 h-3.5 rtl:-scale-x-100" />
      </span>

      <div
        key={tip.id}
        className={phase === 'in' ? 'tip-slide-in' : 'tip-slide-out'}
      >
        <div className="flex items-start gap-3 pe-6">
          <span className="p-2 rounded-lg bg-app-primary-500/10 text-app-primary-500 flex-shrink-0">
            <Icon className="w-4 h-4" />
          </span>
          <div className="flex-1 min-w-0 space-y-1.5">
            <p className="text-[15px] text-foreground leading-snug" dir="auto">
              {tip.title[locale] || tip.title.en}
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed" dir="auto">
              {tip.body[locale] || tip.body.en}
            </p>
          </div>
        </div>
      </div>
    </button>
  )
}
